import React, { useEffect, useMemo, useState } from "react";
import Progressbar from "@/src/components/loading";
import { facilitiesApiService } from "@/src/lib/api-services/facilitiesApiServices";
import SpecializationListFilter from "@/src/modules/find-a-test/specializationListFilter";
import { findTestApiService } from "@/src/lib/api-services/findTestApiService";
import { useRouter } from "next/router";
import { useCustomQuery } from "@/src/lib/hooks/useFetch";
import CustomButton from "@/src/lib/ui/Button";
import CustomAccordion from "@/src/lib/ui/customAccordion";
import { CustomRadioGroup } from "@/src/lib/ui/customRadioGroup";
import CustomCheckbox from "@/src/lib/ui/customCheckbox";

const FilterSearchModal = ({
    handleChangeFacilities,
    searchFilter,
    handleChangeSpecialization,
    handleApplyFilters,
    isMobile,
    clearAllFilters,
    router,
}) => {
    const isAllowHc = router?.allowHc == "1";
    const [isHomeCollection, setIsHomeCollection] = useState(
        Boolean(isAllowHc)
    );
    const [facilitiesOpen, setFacilitiesOpen] = useState(true);
    const [specializationOpen, setSpecializationOpen] = useState(false);
    const { push } = useRouter();
    useEffect(() => {
        if (isHomeCollection !== Boolean(isAllowHc)) {
            setIsHomeCollection(Boolean(isAllowHc));
        }
    }, [isAllowHc]);

    const query = { ...router, allowHc: isHomeCollection ? 1 : 0 };

    const { data, isLoading } = useCustomQuery(
        "specialization-filter-list",
        findTestApiService.getSpecializationList
    );

    const { data: facilitiesList, isLoading: isLoadingFacilities } =
        useCustomQuery(
            "facilities-filter-list",
            facilitiesApiService.facilitiesList
        );

    const memoizedFacilitiesData = useMemo(() => {
        return facilitiesList?.data
            ?.map((item) => item.categories.flat())
            .flat();
    }, [facilitiesList]);

    useEffect(() => {
        push({
            pathname: router.pathname,
            query,
        });
    }, [isHomeCollection]);

    const handleChangeAllowHc = (event) => {
        setIsHomeCollection(event.target.checked);
    };

    const handleFacilitiesToggle = () => {
        setFacilitiesOpen((prev) => {
            const next = !prev;
            if (next) {
                setSpecializationOpen(false);
            }
            return next;
        });
    };

    const handleSpecializationToggle = () => {
        setSpecializationOpen((prev) => {
            const next = !prev;
            if (next) {
                setFacilitiesOpen(false);
            }
            return next;
        });
    };

    return (
        <div className="bg-white shadow-md rounded-md overflow-hidden">
            {/* Heading only shown on desktop */}

            <>
                <h2 className="md:block hidden text-xl font-medium text-white bg-primary py-4 px-3">
                    Filters
                </h2>
                <div className="p-4 border-b border-gray-200 bg-gray-100">
                    <CustomCheckbox
                        checked={isHomeCollection}
                        name="allowHc"
                        label="Home Collection"
                        onChange={(event) => handleChangeAllowHc(event)}
                    />
                </div>
            </>

            <CustomAccordion
                title={isMobile ? "FACILITIES" : "Facilities"}
                className="border-b rounded-none"
                buttonClassName="rounded-none hover:bg-gray-100 font-bold md:text-lg text-base"
                open={facilitiesOpen}
                onClick={handleFacilitiesToggle}
            >
                <div
                    className="md:mt-0"
                    style={{
                        maxHeight: "300px",
                        overflowY: "auto",
                        // scrollbarWidth: "none" /* For Firefox */,
                        // msOverflowStyle: "none" /* For Internet Explorer 10+ */,
                    }}
                >
                    {isLoadingFacilities ? (
                        <Progressbar />
                    ) : (
                        <CustomRadioGroup
                            //  defaultValue={0}
                            name="facility-filter-group"
                            value={searchFilter?.category}
                            onChange={(_, val) => handleChangeFacilities(val)}
                        >
                            <div className="flex items-center gap-4 mb-4 mt-4">
                                <input
                                    type="radio"
                                    value=""
                                    className="w-4 h-4 bg-primary ml-2"
                                    style={{
                                        transform: "scale(1.5)",
                                    }}
                                />

                                <div className="font-normal text-black text-base">
                                    All Facilities
                                </div>
                            </div>

                            {memoizedFacilitiesData?.length > 0 &&
                                memoizedFacilitiesData.map((item) => (
                                    <div
                                        key={item.categoryId}
                                        className="flex items-center gap-4 mb-4"
                                    >
                                        <input
                                            type="radio"
                                            value={item.categoryId}
                                            className="w-4 h-4 bg-primary ml-2"
                                            style={{
                                                transform: "scale(1.5)",
                                            }}
                                        />

                                        <div className="font-normal text-black text-base">
                                            {item.categoryName}
                                        </div>
                                    </div>
                                ))}
                        </CustomRadioGroup>
                    )}
                </div>
            </CustomAccordion>

            <CustomAccordion
                title={isMobile ? "SPECIALIZATION" : "Specialization"}
                className="border-b rounded-none"
                buttonClassName="rounded-none hover:bg-gray-100 font-bold md:text-lg text-base"
                open={specializationOpen}
                onClick={handleSpecializationToggle}
            >
                <div className="md:mt-0 max-h-80 overflow-y-auto">
                    {isLoading ? (
                        <Progressbar />
                    ) : (
                        data?.data?.length > 0 &&
                        data.data.map((item) => (
                            <SpecializationListFilter
                                key={item.specializationId}
                                item={item}
                                handleChangeSpecialization={
                                    handleChangeSpecialization
                                }
                                selectedValues={searchFilter?.specializations}
                            />
                        ))
                    )}
                </div>
            </CustomAccordion>

            {/* Apply Filters button only in mobile view */}
            {isMobile && (
                <div className="p-4">
                    <div className="flex flex-row gap-2">
                        <CustomButton
                            variant="outlined"
                            color="primary"
                            className="w-full"
                            onClick={clearAllFilters}
                        >
                            Clear All
                        </CustomButton>

                        <CustomButton
                            onClick={handleApplyFilters}
                            variant="contained"
                            color="primary"
                            className="w-full bg-primary text-white font-family-bold py-2 px-4 rounded-md"
                        >
                            Apply Filters
                        </CustomButton>
                    </div>
                </div>
            )}
        </div>
    );
};

export default React.memo(FilterSearchModal);
